import { useState } from 'react'
import type { SymptomSummary } from '../lib/types'

interface SummaryViewProps {
  summary: SymptomSummary
  onDownloadPdf: (userMemo?: string) => void
  isGeneratingPdf: boolean
}

const urgencyConfig = {
  high: {
    label: 'すぐに受診してね！',
    emoji: '🚨',
    bgClass: 'bg-gradient-to-r from-light-error/50 to-pet-coral/30 dark:from-dark-error/30 dark:to-pet-coral/20',
    textClass: 'text-red-600 dark:text-dark-error',
    borderClass: 'border-light-error dark:border-dark-error',
  },
  medium: {
    label: '数日以内に受診を',
    emoji: '⚠️',
    bgClass: 'bg-gradient-to-r from-light-warning/50 to-pet-peach/30 dark:from-dark-warning/30 dark:to-pet-peach/20',
    textClass: 'text-amber-600 dark:text-dark-warning',
    borderClass: 'border-light-warning dark:border-dark-warning',
  },
  low: {
    label: '経過観察でOK',
    emoji: '✅',
    bgClass: 'bg-gradient-to-r from-light-success/50 to-pet-mint/30 dark:from-dark-success/30 dark:to-pet-mint/20',
    textClass: 'text-green-600 dark:text-dark-success',
    borderClass: 'border-light-success dark:border-dark-success',
  },
}

const petTypeLabels = {
  dog: 'わんちゃん',
  cat: 'ねこちゃん',
  other: 'ペット',
}

const petEmojis = {
  dog: '🐕',
  cat: '🐈',
  other: '🐰',
}

const providerLabels = {
  openai: 'OpenAI',
  gemini: 'Gemini',
  claude: 'Claude',
}

export function SummaryView({
  summary,
  onDownloadPdf,
  isGeneratingPdf,
}: SummaryViewProps) {
  const [userMemo, setUserMemo] = useState('')
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'shared'>('idle')
  const urgency = urgencyConfig[summary.urgency]
  const date = new Date(summary.createdAt).toLocaleDateString('ja-JP')

  // 共有用テキストを生成
  const generateShareText = () => {
    const petLabel = petTypeLabels[summary.petType]
    const urgencyLabel = urgencyConfig[summary.urgency].label

    return `🐾 ${petLabel}の症状レポート - ${summary.keyword}

📋 緊急度: ${urgency.emoji} ${urgencyLabel}

🩺 主な症状:
${summary.symptoms.map(s => `• ${s}`).join('\n')}

🔍 考えられる原因:
${summary.causes.map(c => `• ${c}`).join('\n')}

✨ おすすめの対応:
${summary.recommendations.map(r => `• ${r}`).join('\n')}
${userMemo ? `\n📝 メモ:\n${userMemo}` : ''}

⚠️ このレポートはAIによる参考情報です。症状が続く場合は必ず獣医師にご相談ください。

📅 作成日: ${date}
🤖 分析: ${providerLabels[summary.provider]}

---
🐾 ペット症状まとめ で作成`
  }

  // 共有機能
  const handleShare = async () => {
    const shareText = generateShareText()

    // Web Share APIが使える場合
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${petTypeLabels[summary.petType]}の症状レポート - ${summary.keyword}`,
          text: shareText,
        })
        setShareStatus('shared')
      } catch (error) {
        // ユーザーがキャンセルした場合など
        console.log('Share cancelled or failed')
      }
    } else {
      // クリップボードにコピー
      try {
        await navigator.clipboard.writeText(shareText)
        setShareStatus('copied')
      } catch {
        // フォールバック: テキストエリアを使用
        const textarea = document.createElement('textarea')
        textarea.value = shareText
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        setShareStatus('copied')
      }
    }

    // 3秒後にステータスをリセット
    setTimeout(() => setShareStatus('idle'), 3000)
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="text-center pb-6 border-b-4 border-dashed border-light-paw dark:border-dark-paw">
        <div className="text-5xl mb-3 animate-bounce-slow">
          {petEmojis[summary.petType]}
        </div>
        <h2 className="text-2xl font-bold gradient-text mb-2">
          {petTypeLabels[summary.petType]}の症状レポート
        </h2>
        <p className="text-sm text-light-text-sub dark:text-dark-text-sub">
          🔍 {summary.keyword} | 📅 {date}
        </p>
        <p className="text-xs text-light-text-sub dark:text-dark-text-sub mt-1">
          🤖 {providerLabels[summary.provider]}で分析
        </p>
      </div>

      {/* 緊急度 */}
      <div
        className={`p-5 rounded-2xl border-2 ${urgency.bgClass} ${urgency.borderClass}`}
      >
        <div className="flex items-center justify-center gap-3">
          <span className="text-3xl">{urgency.emoji}</span>
          <div className="text-center">
            <span className="text-sm text-light-text-sub dark:text-dark-text-sub">緊急度</span>
            <p className={`text-xl font-bold ${urgency.textClass}`}>
              {urgency.label}
            </p>
          </div>
        </div>
      </div>

      {/* 警告 */}
      <div className="p-4 rounded-2xl bg-pet-cream/50 dark:bg-pet-cream/10 border-2 border-dashed border-pet-peach">
        <p className="text-sm text-light-text dark:text-dark-text text-center flex items-center justify-center gap-2">
          <span>💡</span>
          このレポートはAIによる参考情報だよ。心配な時は獣医さんに相談してね！
        </p>
      </div>

      {/* 主な症状 */}
      <Section title="主な症状" emoji="🩺">
        <ul className="space-y-3">
          {summary.symptoms.map((symptom, i) => (
            <li key={i} className="flex items-start gap-3 bg-white/50 dark:bg-dark-bg-sub/50 p-3 rounded-xl">
              <span className="text-light-accent dark:text-dark-accent text-lg">🐾</span>
              <span className="text-light-text dark:text-dark-text">
                {symptom}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      {/* 考えられる原因 */}
      <Section title="考えられる原因" emoji="🔍">
        <ul className="space-y-3">
          {summary.causes.map((cause, i) => (
            <li key={i} className="flex items-start gap-3 bg-white/50 dark:bg-dark-bg-sub/50 p-3 rounded-xl">
              <span className="text-pet-lavender text-lg">💭</span>
              <span className="text-light-text dark:text-dark-text">
                {cause}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      {/* 推奨アクション */}
      <Section title="おすすめの対応" emoji="✨">
        <ul className="space-y-3">
          {summary.recommendations.map((rec, i) => (
            <li key={i} className="flex items-start gap-3 bg-pet-mint/20 dark:bg-pet-mint/10 p-3 rounded-xl">
              <span className="text-light-success dark:text-dark-success text-lg">✅</span>
              <span className="text-light-text dark:text-dark-text">{rec}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* 参照元 */}
      <Section title="参照元" emoji="📚">
        <ul className="space-y-2">
          {summary.sources.map((source, i) => (
            <li key={i}>
              <a
                href={source}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-light-accent dark:text-dark-accent hover:underline break-all flex items-center gap-2"
              >
                <span>🔗</span>
                {source}
              </a>
            </li>
          ))}
        </ul>
      </Section>

      {/* カスタムメモ入力 */}
      <Section title="メモ欄（PDFに記載されます）" emoji="📝">
        <textarea
          value={userMemo}
          onChange={(e) => setUserMemo(e.target.value)}
          placeholder="獣医さんに伝えたいこと、気になる症状の詳細などを入力してね..."
          className="
            w-full p-4 rounded-xl
            bg-white/70 dark:bg-dark-bg-sub/70
            border-2 border-dashed border-pet-lavender dark:border-pet-lavender/50
            focus:border-light-accent dark:focus:border-dark-accent
            focus:outline-none focus:ring-2 focus:ring-light-accent/30 dark:focus:ring-dark-accent/30
            text-light-text dark:text-dark-text
            placeholder-light-text-sub/60 dark:placeholder-dark-text-sub/60
            resize-none transition-all duration-300
            min-h-[100px]
          "
          rows={3}
        />
        <p className="text-xs text-light-text-sub dark:text-dark-text-sub mt-2 flex items-center gap-1">
          <span>💡</span>
          食事の変化、いつから症状があるか、最近の出来事などを書くと獣医さんに伝わりやすいよ
        </p>
      </Section>

      {/* 免責事項（目立つように） */}
      <div className="p-5 rounded-2xl bg-gradient-to-r from-light-warning/40 to-pet-peach/40 dark:from-dark-warning/30 dark:to-pet-peach/20 border-2 border-light-warning dark:border-dark-warning">
        <div className="flex items-start gap-3">
          <span className="text-3xl">⚠️</span>
          <div>
            <h4 className="font-bold text-amber-700 dark:text-dark-warning mb-2">
              大切なおねがい
            </h4>
            <p className="text-sm text-light-text dark:text-dark-text leading-relaxed">
              このレポートはAIによる<strong>参考情報</strong>であり、<strong>医療診断ではありません</strong>。
              症状が続く場合や心配な場合は、<strong>必ず獣医師さんに相談してください</strong>。
              AIの情報は100%正確ではないため、専門家の判断が最も重要です。🏥
            </p>
          </div>
        </div>
      </div>

      {/* PDFダウンロードボタン */}
      <button
        onClick={() => onDownloadPdf(userMemo || undefined)}
        disabled={isGeneratingPdf}
        className="
          w-full py-4 px-6 rounded-2xl font-bold text-lg
          bg-gradient-to-r from-pet-mint to-light-secondary
          dark:from-dark-success dark:to-dark-secondary
          text-white shadow-cute
          hover:shadow-cute-lg hover:scale-[1.02]
          active:scale-[0.98]
          disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
          transition-all duration-300
          flex items-center justify-center gap-3
        "
      >
        {isGeneratingPdf ? (
          <>
            <span className="loading-paw">🐾</span>
            <span className="loading-paw">🐾</span>
            <span className="loading-paw">🐾</span>
            <span className="ml-2">PDF作成中...</span>
          </>
        ) : (
          <>
            <span className="text-2xl">📄</span>
            PDFでダウンロード
          </>
        )}
      </button>

      {/* 共有ボタン */}
      <button
        onClick={handleShare}
        className="
          w-full py-4 px-6 rounded-2xl font-bold text-lg
          bg-gradient-to-r from-pet-lavender to-light-accent
          dark:from-pet-lavender/80 dark:to-dark-accent
          text-white shadow-cute
          hover:shadow-cute-lg hover:scale-[1.02]
          active:scale-[0.98]
          transition-all duration-300
          flex items-center justify-center gap-3
        "
      >
        {shareStatus === 'copied' ? (
          <>
            <span className="text-2xl">✅</span>
            コピーしました！
          </>
        ) : shareStatus === 'shared' ? (
          <>
            <span className="text-2xl">✅</span>
            共有しました！
          </>
        ) : (
          <>
            <span className="text-2xl">📤</span>
            レポートを共有
          </>
        )}
      </button>

      {/* 共有のヒント */}
      <p className="text-center text-xs text-light-text-sub dark:text-dark-text-sub">
        💡 共有ボタンでテキスト形式のレポートをLINEやメールで送れるよ
      </p>
    </div>
  )
}

function Section({
  title,
  emoji,
  children,
}: {
  title: string
  emoji: string
  children: React.ReactNode
}) {
  return (
    <div>
      <h3 className="font-bold text-light-text dark:text-dark-text flex items-center gap-2 mb-4">
        <span className="text-xl">{emoji}</span>
        <span className="bg-gradient-to-r from-light-accent to-light-secondary dark:from-dark-accent dark:to-dark-secondary bg-clip-text text-transparent">
          {title}
        </span>
      </h3>
      {children}
    </div>
  )
}
